import { useState, useEffect, lazy, Suspense } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faWallet,
  faGlobe,
  faMapMarkerAlt,
  faCompactDisc,
  faMusic,
  faBuilding,
  faChartLine,
  faTree,
  faSnowman,
  faArrowTrendUp,
} from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import { authSelector } from '../features/auth/slice';

// ====== Lazy Load (차트 무거움 분리) ======
const MonthlyAmountChart = lazy(() =>
  import('../components/MonthlyAmountChart')
);
const SeasonAmountChart = lazy(() => import('../components/SeasonAmountChart'));
const GenreTrendChart = lazy(() => import('../components/GenreTrendChart'));
const Chart = lazy(() => import('react-apexcharts'));
// =========================================

const LoadingSpinner = () => (
  <div className="flex items-center gap-2 text-slate-400 text-sm">
    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-sky-500" />
    <span>불러오는 중...</span>
  </div>
);

function DashBoardPage() {
  const { me } = useSelector(authSelector.selectAuthState);
  const isAdmin = me?.adminYn === 'Y';

  // 초기 월 리스트 로딩
  const [monthListLoading, setMonthListLoading] = useState(true);

  // Loading states
  const [loading1_1, setLoading1_1] = useState(false);
  const [loading1_2, setLoading1_2] = useState(false);
  const [loading2_1, setLoading2_1] = useState(false);
  const [loading2_2, setLoading2_2] = useState(false);
  const [loading2_3, setLoading2_3] = useState(false);
  const [loading3_1, setLoading3_1] = useState(false);
  const [loading3_2, setLoading3_2] = useState(false);
  const [loading3_3, setLoading3_3] = useState(false);
  const [loadingTopClients, setLoadingTopClients] = useState(false);

  // Data states
  const [monthList, setMonthList] = useState([]);
  const [month, setMonth] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  const [sumAmounts, setSumAmounts] = useState(0);
  const [percentChange, setPercentChange] = useState(0);
  const [sumKoreaAmounts, setSumKoreaAmounts] = useState(0);
  const [percentChangeKorea, setPercentChangeKorea] = useState(0);
  const [sumGlobalAmounts, setSumGlobalAmounts] = useState(0);
  const [percentChangeGlobal, setPercentChangeGlobal] = useState(0);

  const [prevMonthNumber, setPrevMonthNumber] = useState();
  const [topClients, setTopClients] = useState([]);
  const [topAlbums, setTopAlbums] = useState([]);
  const [topSongs, setTopSongs] = useState([]);
  const [seasonData, setSeasonData] = useState([]);
  const [seasonAllData, setSeasonAllData] = useState([]);
  const [genreData, setGenreData] = useState([]);
  const [dashboardTopClients, setDashboardTopClients] = useState([]);

  // ===============================
  // 초기 월 리스트 조회
  // ===============================
  useEffect(() => {
    if (!me) return;

    setMonthListLoading(true);

    axios
      .get('/ByClient/getMonthList', {
        params: isAdmin ? {} : { clientId: me.id },
      })
      .then((res) => {
        const list = res.data || [];
        setMonthList(list);
        if (list.length > 0) setMonth(list[0]);
      })
      .catch((err) => console.error('월 리스트 조회 오류:', err))
      .finally(() => setMonthListLoading(false));
  }, [me, isAdmin]);

  // 현재 index
  useEffect(() => {
    setCurrentIndex(monthList.indexOf(month));
  }, [month, monthList]);

  // ===============================
  // sumAmounts
  // ===============================
  useEffect(() => {
    if (!me || !month) return;

    setLoading1_1(true);
    axios
      .get('/ByClient/dashboardSumAmounts', {
        params: {
          calculateMonth: month,
          clientId: me.id,
          adminYn: isAdmin ? 'Y' : 'N',
        },
      })
      .then((res) => {
        setSumAmounts(res.data.sumAmounts);
        setPercentChange(res.data.percentChange);
      })
      .catch((err) => console.error('플랫폼 정산총액 합계 조회 오류:', err))
      .finally(() => setLoading1_1(false));
  }, [me, month, isAdmin]);

  // ===============================
  // 국내/해외
  // ===============================
  useEffect(() => {
    if (!me || !month) return;

    setLoading1_2(true);
    axios
      .get('/ByClient/dashboardSumTypes', {
        params: {
          calculateMonth: month,
          clientId: me.id,
          adminYn: isAdmin ? 'Y' : null,
        },
      })
      .then((res) => {
        setSumKoreaAmounts(res.data.sumKoreaAmounts);
        setPercentChangeKorea(res.data.percentChangeKorea);
        setSumGlobalAmounts(res.data.sumGlobalAmounts);
        setPercentChangeGlobal(res.data.percentChangeGlobal);
      })
      .catch((err) => console.error('국내/해외 플랫폼 합계 조회 오류:', err))
      .finally(() => setLoading1_2(false));
  }, [me, month, isAdmin]);

  // ===============================
  // 급상승 Clients/Albums/Songs
  // ===============================
  useEffect(() => {
    if (!me || !month) return;

    setLoading2_1(true);
    axios
      .get('/ByClient/topRisingClients', {
        params: {
          calculateMonth: month,
          clientId: me.id,
          adminYn: isAdmin ? 'Y' : 'N',
        },
      })
      .then((res) => setTopClients(res.data.items))
      .catch((err) => console.error('급상승 권리사/아티스트 조회 오류:', err))
      .finally(() => setLoading2_1(false));
  }, [me, month, isAdmin]);

  useEffect(() => {
    if (!me || !month) return;

    setLoading2_2(true);
    axios
      .get('/ByClient/topRisingAlbums', {
        params: {
          calculateMonth: month,
          clientId: me.id,
          adminYn: isAdmin ? 'Y' : null,
        },
      })
      .then((res) => setTopAlbums(res.data.items))
      .catch((err) => console.error('급상승 앨범 조회 오류:', err))
      .finally(() => setLoading2_2(false));
  }, [me, month, isAdmin]);

  useEffect(() => {
    if (!me || !month) return;

    setLoading2_3(true);
    axios
      .get('/ByClient/topRisingSongs', {
        params: {
          calculateMonth: month,
          clientId: me.id,
          adminYn: isAdmin ? 'Y' : null,
        },
      })
      .then((res) => setTopSongs(res.data.items))
      .catch((err) => console.error('급상승 곡 조회 오류:', err))
      .finally(() => setLoading2_3(false));
  }, [me, month, isAdmin]);

  // ===============================
  // 관리자: TOP Clients / 권리사: 내 계절별 점유율
  // ===============================
  useEffect(() => {
    if (!me) return;

    if (isAdmin) {
      setLoadingTopClients(true);
      axios
        .get('/ByClient/dashboardTopClients')
        .then((res) => setDashboardTopClients(res.data))
        .catch((err) => console.error('권리사 누적정산 TOP4 조회 오류:', err))
        .finally(() => setLoadingTopClients(false));
    } else {
      setLoading3_1(true);
      axios
        .get('/ByClient/dashboardSeasonAmounts')
        .then((res) => setSeasonData(res.data))
        .catch((err) => console.error('내 계절별 점유율 조회 오류:', err))
        .finally(() => setLoading3_1(false));
    }
  }, [me, isAdmin]);

  // 전체 계절 점유율
  useEffect(() => {
    setLoading3_2(true);
    axios
      .get('/ByClient/dashboardSeasonAmountsTotal')
      .then((res) => setSeasonAllData(res.data))
      .catch((err) => console.error('전체 계절 점유율 조회 오류:', err))
      .finally(() => setLoading3_2(false));
  }, []);

  // 장르별 트렌드
  useEffect(() => {
    setLoading3_3(true);
    axios
      .get('/ByClient/dashboardGenreTrend')
      .then((res) => setGenreData(res.data || []))
      .catch((err) => console.error('장르 트렌드 조회 오류:', err))
      .finally(() => setLoading3_3(false));
  }, []);

  useEffect(() => {
    if (!month) return;
    const date = new Date(month);
    if (!isNaN(date)) setPrevMonthNumber(date.getMonth() + 1);
  }, [month]);

  const handlePrevMonth = () => {
    if (currentIndex < monthList.length - 1)
      setMonth(monthList[currentIndex + 1]);
  };
  const handleNextMonth = () => {
    if (currentIndex > 0) setMonth(monthList[currentIndex - 1]);
  };

  const formatPercent = (v) =>
    v >= 1000 ? `${Math.round(v / 1000)}K%` : `${Math.round(v)}%`;

  const formatTitle = (d) => {
    const [y, mo] = d.split('-');
    return `${y}년 ${parseInt(mo, 10)}월 대시보드`;
  };

  return (
    <>
        {monthListLoading || !month ? (
          <div className="w-full h-[calc(100vh-96px)] flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-sky-500 mb-4" />
            <div className="text-slate-400 text-sm">대시보드 초기화 중...</div>
          </div>
        ) : (
          <>
            {/* 월 제목 */}
            <div className="flex mb-8 justify-center items-center gap-5">
              <button
                onClick={handlePrevMonth}
                disabled={currentIndex === monthList.length - 1}
                className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>

              <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                {formatTitle(month)}
              </h1>

              <button
                onClick={handleNextMonth}
                disabled={currentIndex === 0}
                className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>

            {/* 첫째 행 - 정산 요약 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {/* 플랫폼 정산총액 */}
              <div className="flex flex-col rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.05]">
                  <FontAwesomeIcon icon={faWallet} className="text-slate-400 text-sm" />
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                    플랫폼 정산총액
                  </span>
                </div>
                <div className="flex flex-col justify-center items-center h-32 px-5">
                  {loading1_1 ? (
                    <LoadingSpinner />
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1.5 mb-1">
                        <span className="text-3xl font-bold text-white">
                          {sumAmounts.toLocaleString('ko-KR')}
                        </span>
                        <span className="text-base font-medium text-slate-500">원</span>
                      </div>
                      <div className="text-xs text-slate-400 mb-2.5">
                        ({prevMonthNumber}월 판매분)
                      </div>
                      <span
                        className={`text-xs px-2.5 py-0.5 rounded-full font-medium
                          ${percentChange > 0
                            ? 'bg-sky-500/10 text-sky-400'
                            : percentChange < 0
                            ? 'bg-rose-500/10 text-rose-400'
                            : 'bg-white/5 text-slate-400'
                          }`}>
                        {percentChange > 0 && '▲ '}
                        {percentChange < 0 && '▼ '}
                        {Math.abs(percentChange).toLocaleString()}% 이전월 대비
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* 국내 플랫폼 */}
              <div className="flex flex-col rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.05]">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="text-slate-400 text-sm" />
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                    국내 플랫폼
                  </span>
                </div>
                <div className="flex flex-col justify-center items-center h-32 px-5">
                  {loading1_2 ? (
                    <LoadingSpinner />
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1.5 mb-1">
                        <span className="text-3xl font-bold text-white">
                          {sumKoreaAmounts.toLocaleString('ko-KR')}
                        </span>
                        <span className="text-base font-medium text-slate-500">원</span>
                      </div>
                      <div className="text-xs text-slate-400 mb-2.5">
                        ({sumAmounts > 0
                          ? Math.round((sumKoreaAmounts / sumAmounts) * 1000) / 10 + '%'
                          : '0%'})
                      </div>
                      <span
                        className={`text-xs px-2.5 py-0.5 rounded-full font-medium
                          ${percentChangeKorea > 0
                            ? 'bg-sky-500/10 text-sky-400'
                            : percentChangeKorea < 0
                            ? 'bg-rose-500/10 text-rose-400'
                            : 'bg-white/5 text-slate-400'
                          }`}>
                        {percentChangeKorea > 0 && '▲ '}
                        {percentChangeKorea < 0 && '▼ '}
                        {Math.abs(percentChangeKorea).toLocaleString()}% 이전월 대비
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* 해외 플랫폼 */}
              <div className="flex flex-col rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.05]">
                  <FontAwesomeIcon icon={faGlobe} className="text-slate-400 text-sm" />
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                    해외 플랫폼
                  </span>
                </div>
                <div className="flex flex-col justify-center items-center h-32 px-5">
                  {loading1_2 ? (
                    <LoadingSpinner />
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1.5 mb-1">
                        <span className="text-3xl font-bold text-white">
                          {sumGlobalAmounts.toLocaleString('ko-KR')}
                        </span>
                        <span className="text-base font-medium text-slate-500">원</span>
                      </div>
                      <div className="text-xs text-slate-400 mb-2.5">
                        ({sumAmounts > 0
                          ? Math.round((sumGlobalAmounts / sumAmounts) * 1000) / 10 + '%'
                          : '0%'})
                      </div>
                      <span
                        className={`text-xs px-2.5 py-0.5 rounded-full font-medium
                          ${percentChangeGlobal > 0
                            ? 'bg-sky-500/10 text-sky-400'
                            : percentChangeGlobal < 0
                            ? 'bg-rose-500/10 text-rose-400'
                            : 'bg-white/5 text-slate-400'
                          }`}>
                        {percentChangeGlobal > 0 && '▲ '}
                        {percentChangeGlobal < 0 && '▼ '}
                        {Math.abs(percentChangeGlobal).toLocaleString()}% 이전월 대비
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* 둘째 행 - 급상승 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
              {/* 급상승 권리사/아티스트 */}
              <div className="flex flex-col rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.05]">
                  <FontAwesomeIcon icon={faBuilding} className="text-rose-400 text-sm" />
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                    {me?.adminYn === 'Y' ? '급상승 권리사' : '급상승 아티스트'}
                  </span>
                </div>
                <div className="flex items-center justify-center px-5 py-4 min-h-[11rem]">
                  {loading2_1 ? (
                    <LoadingSpinner />
                  ) : topClients.length > 0 ? (
                    <div className="w-full flex flex-col gap-2">
                      {topClients.map((c, idx) => (
                        <div
                          key={c.clientId || c.artistId}
                          className="flex justify-between items-center">
                          <div className="relative group flex items-center gap-1.5 max-w-[70%]">
                            <span className="w-4 text-center text-slate-500 text-xs font-mono">
                              {idx + 1}
                            </span>
                            <span className="truncate text-sm text-slate-200 font-medium">
                              {c.name}
                            </span>
                            <div className="absolute left-6 -top-9 z-50 hidden group-hover:flex flex-col items-center">
                              <div className="bg-slate-800 text-white text-xs px-2.5 py-1 rounded-lg shadow-lg whitespace-nowrap">
                                {c.name}
                              </div>
                              <div className="w-2 h-2 bg-slate-800 rotate-45 mt-[-4px]" />
                            </div>
                          </div>
                          <span
                            className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${
                              Number(c.changeRate) > 0
                                ? 'bg-sky-500/10 text-sky-400'
                                : Number(c.changeRate) < 0
                                ? 'bg-rose-500/10 text-rose-400'
                                : 'bg-white/5 text-slate-400'
                            }`}>
                            {Number(c.changeRate) > 0 && '▲ '}
                            {Number(c.changeRate) < 0 && '▼ '}
                            {formatPercent(Math.abs(Number(c.changeRate)))}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-slate-400 text-sm text-center w-full">
                      데이터 없음
                    </div>
                  )}
                </div>
              </div>

              {/* 급상승 앨범 */}
              <div className="flex flex-col rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.05]">
                  <FontAwesomeIcon icon={faCompactDisc} className="text-rose-400 text-sm" />
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                    급상승 앨범
                  </span>
                </div>
                <div className="flex items-center justify-center px-5 py-4 min-h-[11rem]">
                  {loading2_2 ? (
                    <LoadingSpinner />
                  ) : topAlbums.length > 0 ? (
                    <div className="w-full flex flex-col gap-2">
                      {topAlbums.map((a, idx) => (
                        <div key={a.albumId} className="flex justify-between items-center">
                          <div className="relative group flex items-center gap-1.5 max-w-[70%]">
                            <span className="w-4 text-center text-slate-500 text-xs font-mono">
                              {idx + 1}
                            </span>
                            <span className="truncate text-sm text-slate-200 font-medium">
                              {a.name}{' '}
                              <span className="text-slate-400 font-normal">- {a.artist}</span>
                            </span>
                            <div className="absolute left-6 -top-9 z-50 hidden group-hover:flex flex-col items-center">
                              <div className="bg-slate-800 text-white text-xs px-2.5 py-1 rounded-lg shadow-lg whitespace-nowrap">
                                {a.name}{' '}
                                <span className="text-slate-400">- {a.artist}</span>
                              </div>
                              <div className="w-2 h-2 bg-slate-800 rotate-45 mt-[-4px]" />
                            </div>
                          </div>
                          <span
                            className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${
                              Number(a.changeRate) > 0
                                ? 'bg-sky-500/10 text-sky-400'
                                : Number(a.changeRate) < 0
                                ? 'bg-rose-500/10 text-rose-400'
                                : 'bg-white/5 text-slate-400'
                            }`}>
                            {Number(a.changeRate) > 0 && '▲ '}
                            {Number(a.changeRate) < 0 && '▼ '}
                            {formatPercent(Math.abs(Number(a.changeRate)))}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-slate-400 text-sm text-center w-full">
                      데이터 없음
                    </div>
                  )}
                </div>
              </div>

              {/* 급상승 곡 */}
              <div className="flex flex-col rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.05]">
                  <FontAwesomeIcon icon={faMusic} className="text-rose-400 text-sm" />
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                    급상승 곡
                  </span>
                </div>
                <div className="flex items-center justify-center px-5 py-4 min-h-[11rem]">
                  {loading2_3 ? (
                    <LoadingSpinner />
                  ) : topSongs.length > 0 ? (
                    <div className="w-full flex flex-col gap-2">
                      {topSongs.map((s, idx) => (
                        <div key={s.songId} className="flex justify-between items-center">
                          <div className="relative group flex items-center gap-1.5 max-w-[70%]">
                            <span className="w-4 text-center text-slate-500 text-xs font-mono">
                              {idx + 1}
                            </span>
                            <span className="truncate text-sm text-slate-200 font-medium">
                              {s.name}{' '}
                              <span className="text-slate-400 font-normal">- {s.artist}</span>
                            </span>
                            <div className="absolute left-6 -top-9 z-50 hidden group-hover:flex flex-col items-center">
                              <div className="bg-slate-800 text-white text-xs px-2.5 py-1 rounded-lg shadow-lg whitespace-nowrap">
                                {s.name}{' '}
                                <span className="text-slate-400">- {s.artist}</span>
                              </div>
                              <div className="w-2 h-2 bg-slate-800 rotate-45 mt-[-4px]" />
                            </div>
                          </div>
                          <span
                            className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${
                              Number(s.changeRate) > 0
                                ? 'bg-sky-500/10 text-sky-400'
                                : Number(s.changeRate) < 0
                                ? 'bg-rose-500/10 text-rose-400'
                                : 'bg-white/5 text-slate-400'
                            }`}>
                            {Number(s.changeRate) > 0 && '▲ '}
                            {Number(s.changeRate) < 0 && '▼ '}
                            {formatPercent(Math.abs(Number(s.changeRate)))}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-slate-400 text-sm text-center w-full">
                      데이터 없음
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 셋째 행 - 차트 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
              {/* 1 */}
              <div className="flex flex-col rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
                {isAdmin ? (
                  <>
                    <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.05]">
                      <FontAwesomeIcon icon={faChartLine} className="text-sky-500 text-sm" />
                      <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                        권리사 누적 정산 TOP4
                      </span>
                    </div>
                    <div className="flex w-full h-36 justify-center items-center">
                      {loadingTopClients ? (
                        <LoadingSpinner />
                      ) : dashboardTopClients.length > 0 ? (
                        <div className="w-full">
                          <Suspense fallback={<LoadingSpinner />}>
                            <Chart
                              series={[
                                {
                                  name: '정산액',
                                  data: dashboardTopClients.map((c) => c.amount),
                                },
                              ]}
                              options={{
                                chart: {
                                  type: 'bar',
                                  toolbar: { show: false },
                                  background: 'transparent',
                                },
                                theme: { mode: 'dark' },
                                xaxis: {
                                  categories: dashboardTopClients.map((c) => c.clientName),
                                  labels: {
                                    formatter: (val) =>
                                      val.length > 3 ? val.slice(0, 3) + '…' : val,
                                    style: { fontSize: '12px', colors: '#94a3b8' },
                                  },
                                  axisBorder: { show: false },
                                  axisTicks: { show: false },
                                },
                                yaxis: {
                                  labels: { show: false },
                                  axisTicks: { show: false },
                                  axisBorder: { show: false },
                                },
                                tooltip: {
                                  theme: 'dark',
                                  x: {
                                    formatter: (_val, { dataPointIndex }) =>
                                      dashboardTopClients[dataPointIndex].clientName,
                                  },
                                  y: { formatter: (val) => val.toLocaleString() },
                                },
                                dataLabels: {
                                  enabled: true,
                                  formatter: (val) => val.toLocaleString(),
                                  offsetY: -20,
                                  style: { fontSize: '11px', colors: ['#94a3b8'] },
                                },
                                plotOptions: {
                                  bar: {
                                    dataLabels: { position: 'top' },
                                    borderRadius: 4,
                                  },
                                },
                                fill: { colors: ['#0ea5e9'] },
                                grid: { borderColor: 'rgba(255,255,255,0.05)', padding: { left: 10, right: 0 } },
                              }}
                              type="bar"
                              width="100%"
                              height={150}
                            />
                          </Suspense>
                        </div>
                      ) : (
                        <div className="text-slate-400 text-sm text-center">데이터 없음</div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.05]">
                      <FontAwesomeIcon icon={faTree} className="text-sky-500 text-sm" />
                      <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                        내 계절별 정산 점유율
                      </span>
                    </div>
                    <div className="flex h-36 justify-center items-center">
                      {loading3_1 ? (
                        <LoadingSpinner />
                      ) : seasonData.length > 0 ? (
                        <Suspense fallback={<LoadingSpinner />}>
                          <SeasonAmountChart data={seasonData} />
                        </Suspense>
                      ) : (
                        <div className="text-slate-400 text-sm text-center">데이터 없음</div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* 2 */}
              <div className="flex flex-col rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.05]">
                  <FontAwesomeIcon icon={faSnowman} className="text-sky-500 text-sm" />
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                    계절별 정산 점유율
                  </span>
                </div>
                <div className="flex h-36 justify-center items-center">
                  {loading3_2 ? (
                    <LoadingSpinner />
                  ) : seasonAllData.length > 0 ? (
                    <Suspense fallback={<LoadingSpinner />}>
                      <SeasonAmountChart data={seasonAllData} />
                    </Suspense>
                  ) : (
                    <div className="text-slate-400 text-sm text-center">데이터 없음</div>
                  )}
                </div>
              </div>

              {/* 3 */}
              <div className="flex flex-col rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.05]">
                  <FontAwesomeIcon icon={faArrowTrendUp} className="text-sky-500 text-sm" />
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                    장르별 정산 점유율
                  </span>
                </div>
                <div className="flex h-36 justify-center items-center">
                  {loading3_3 ? (
                    <LoadingSpinner />
                  ) : genreData.length > 0 ? (
                    <Suspense fallback={<LoadingSpinner />}>
                      <GenreTrendChart data={genreData} />
                    </Suspense>
                  ) : (
                    <div className="text-slate-400 text-sm text-center">데이터 없음</div>
                  )}
                </div>
              </div>
            </div>

            {/* 월별 정산총액 변동 */}
            <div className="mt-4">
              <div className="flex flex-col rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.05]">
                  <FontAwesomeIcon icon={faChartLine} className="text-slate-400 text-sm" />
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                    월별 정산총액 변동
                  </span>
                </div>
                <div className="flex justify-center items-center w-full pt-4">
                  <Suspense fallback={<LoadingSpinner />}>
                    <MonthlyAmountChart me={me} />
                  </Suspense>
                </div>
              </div>
            </div>
          </>
        )}
    </>
  );
}

export default DashBoardPage;
